<template lang="pug">
  yz-card.px-6.py-5(color="white")
    .d-flex.justify-space-between.align-center.mb-3
      .yz-heading-8 {{ $t('panel.payments') }}
      yz-btn(:label="$t('add')" size="sm" outlined, prepend-icon="$yzi-plus" icon-color="yz-neutral-500" background-color="white" @click="onAddPayment")

    // Empty state for payments
    yz-empty-state-card(v-if="paymentsData.length === 0" icon="$yzi-file-question-02" icon-size="32px" icon-color="yz-neutral-500" border-color="yz-neutral-400" :title="$t('panel.no_payment_added')" :description="$t('panel.added_payment_will_show_here')" :action-label="$t('panel.add_payment')" action-size="sm" @action-click="onAddPayment")

    // Payments Data Table
    yz-data-table(v-if="paymentsData.length > 0" :headers="paymentHeaders" :items="paymentsData" :loading="isRefreshing" :items-per-page="-1")
      template(v-slot:item.receivedAmount="{ item }")
        span {{ item.receivedAmount | displayPrice($currency) }}

      template(v-slot:item.documents="{ item }")
        // Badge with count
        yz-badge.cursor-pointer(v-if="item.documents.files.length > 0" border, size="sm" color="neutral-100" text-color="neutral-700" @click="onViewDocuments(item)")
          yz-icon(size="12px" color="yz-neutral-500") $yzi-attachment-01
          // Stacked thumbnails for first 3 items
          .d-flex.align-center.ml-1
            div(v-for="(file, index) in item.documents.files.slice(0, 3)" :key="file.hid" :class="{ 'has-thumbnail': file.thumbnail }")
              yz-img(v-if="file.thumbnail" :src="file.thumbnail" :alt="file.filename" style="width: 15px; height: 15px; border-radius: 4px; border: 1px solid #E5E5E5; object-fit: cover;")
              .file-icon(v-else style="width: 10px; height: 10px; border-radius: 4px; border: 1px solid #E5E5E5; background: #F5F5F5; display: flex; align-items: center; justify-content: center;")
                yz-icon(size="12px" color="yz-neutral-500") {{ file.fileCategory === 'PDF' ? '$yzi-file-05' : '$yzi-image-01' }}
          span.ml-1 {{ item.documents.count }} files

      template(v-slot:item.actions="{ item }")
        yz-menu(offset-y attach)
          template(v-slot:activator="{ on, attrs }")
            yz-btn(v-bind="attrs" v-on="on" icon="$yzi-dots-horizontal" size="sm" icon-only background-color="white")
          yz-list
            yz-list-item(@click="onViewPayment(item)")
              div {{ $t('panel.view') }}
            yz-divider(color="yz-neutral-300")
            yz-list-item(@click="onDeletePayment(item)")
              .text-danger-500 {{ $t('delete') }}

    // Add Payment Dialog
    add-payment-dialog(v-if="showAddPaymentDialog" v-model="showAddPaymentDialog" :parent-claim="parentClaim" :parent-payment="selectedPayment" @updated="onPaymentUpdated")

    // Delete Payment Confirmation Dialog
    yz-delete-confirmation-dialog(v-if="showDeletePaymentDialog" v-model="showDeletePaymentDialog" item="payment" :loading="isDeletingPayment" @confirmed="onConfirmDeletePayment" @canceled="onCancelDeletePayment")
      yz-card.pa-4.mt-4(outlined color="yz-neutral-200")
        .yz-medium-body-sm.mb-3 {{ $t('panel.payment_details') }}
        .d-flex.flex-column.gap-2
          .d-flex(v-if="paymentToDelete.receivedAmount")
            .yz-regular-body-sm.text-neutral-500(style="min-width: 120px;") {{ $t('panel.received_amount') }}:
            .yz-medium-body-sm.text-neutral-700 {{ paymentToDelete.receivedAmount | displayPrice($currency) }}
          .d-flex(v-if="paymentToDelete.method")
            .yz-regular-body-sm.text-neutral-500(style="min-width: 120px;") {{ $t('panel.method') }}
            .yz-medium-body-sm.text-neutral-700.capitalize {{ paymentToDelete.method }}
          .d-flex(v-if="paymentToDelete.date")
            .yz-regular-body-sm.text-neutral-500(style="min-width: 120px;") {{ $t('panel.date') }}:
            .yz-medium-body-sm.text-neutral-700 {{ paymentToDelete.date }}
          .d-flex(v-if="paymentToDelete.paymentNotes")
            .yz-regular-body-sm.text-neutral-500(style="min-width: 120px;") {{ $t('panel.notes') }}
            .yz-medium-body-sm.text-neutral-700 {{ paymentToDelete.paymentNotes }}
          .d-flex(v-if="paymentToDelete.referenceNo")
            .yz-regular-body-sm.text-neutral-500(style="min-width: 120px;") {{ $t('panel.reference_no') }}
            .yz-medium-body-sm.text-neutral-700 {{ paymentToDelete.referenceNo }}

    // View Images Dialog
    view-images-dialog(v-if="showViewImagesDialog" :open.sync="showViewImagesDialog" :images="documentImages")
</template>

<script>
import { DateTime } from 'luxon'
import { mapGetters, mapActions } from 'vuex'
import { generatePdfThumbnail } from '@/utils'
import AddPaymentDialog from '@/components/clinic/panel/dialogs/AddPaymentDialog.vue'
import ViewImagesDialog from '@/components/dialogs/ViewImagesDialog.vue'

export default {
  components: { AddPaymentDialog, ViewImagesDialog },
  props: {
    parentClaim: {
      type: Object,
      required: true
    },
    isRefreshing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      paymentHeaders: [
        { text: this.$t('panel.received_amount'), value: 'receivedAmount', align: 'start' },
        { text: this.$t('panel.payment_notes_label'), value: 'paymentNotes', align: 'start' },
        { text: this.$t('panel.date'), value: 'date', align: 'start' },
        { text: this.$t('panel.documents'), value: 'documents', align: 'start', sortable: false },
        { text: '', value: 'actions', align: 'end', sortable: false, width: '56px' }
      ],
      showAddPaymentDialog: false,
      showDeletePaymentDialog: false,
      showViewImagesDialog: false,
      paymentToDelete: null,
      selectedPayment: null,
      selectedDocuments: [],
      isDeletingPayment: false
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    paymentsData() {
      if (!this.parentClaim?.payments || !Array.isArray(this.parentClaim.payments)) {
        return []
      }

      return this.parentClaim.payments.map(payment => ({
        originalData: payment,
        id: payment.uid,
        receivedAmount: payment.amount,
        paymentNotes: payment.notes,
        date: this.formatDate(payment.created),
        referenceNo: payment.ref_no,
        method: payment.method,
        type: payment.type,
        documents: {
          count: payment.documents?.length || 0,
          files: (payment.documents || []).map(doc => ({
            ...doc,
            fileCategory: this.getFileCategory(doc.type),
            thumbnail: null,
            thumbnailLoading: false
          }))
        }
      }))
    },
    documentImages() {
      return this.selectedDocuments.filter(doc => doc.fileCategory === 'Image').map(doc => doc.file)
    }
  },
  watch: {
    paymentsData: {
      handler() {
        this.$nextTick(() => {
          this.generatePaymentThumbnails()
        })
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    onAddPayment() {
      this.selectedPayment = null
      this.showAddPaymentDialog = true
    },
    onPaymentUpdated() {
      this.$emit('updated')
    },
    onViewPayment(item) {
      this.selectedPayment = item.originalData
      this.showAddPaymentDialog = true
    },
    onDeletePayment(item) {
      this.paymentToDelete = item
      this.showDeletePaymentDialog = true
    },
    async onConfirmDeletePayment() {
      if (!this.paymentToDelete?.originalData?.uid) {
        await this.showMessage(['error', this.$t('panel.invalid_payment_data')])
        return
      }

      this.isDeletingPayment = true
      try {
        await this.$axios.delete(`/panels/${this.company.slug}/payments/${this.paymentToDelete.originalData.uid}`)
        await this.showMessage(['success', this.$t('panel.payment_deleted')])
        this.$emit('updated')
        this.showDeletePaymentDialog = false
        this.paymentToDelete = null
      } catch (error) {
        console.error('Error deleting payment:', error)
        await this.showMessage(['error', error.response?.data?.message || this.$t('panel.failed_delete_payment')])
      } finally {
        this.isDeletingPayment = false
      }
    },
    onCancelDeletePayment() {
      this.showDeletePaymentDialog = false
      this.paymentToDelete = null
    },
    onViewDocuments(item) {
      if (item.documents.files.length > 0) {
        this.selectedDocuments = item.documents.files
        this.showViewImagesDialog = true
      }
    },
    generatePaymentThumbnails() {
      this.paymentsData.forEach(payment => {
        payment.documents.files.forEach(file => {
          if (file.fileCategory === 'PDF' && !file.thumbnail && !file.thumbnailLoading) {
            file.fileUrl = file.file
            generatePdfThumbnail(file)
          }
          if (file.fileCategory === 'Image' && !file.thumbnail) {
            file.thumbnail = file.file
          }
        })
      })
    },
    formatDate(isoString) {
      if (!isoString) return null
      const date = DateTime.fromISO(isoString)
      return date.isValid ? date.toFormat('dd MMMM yyyy, HH:mm') : null
    },
    getFileCategory(mimeType) {
      if (!mimeType) return 'Unknown'
      if (mimeType.startsWith('image/')) return 'Image'
      if (mimeType === 'application/pdf') return 'PDF'
      return 'Other'
    }
  }
}
</script>

<style scoped></style>
